import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createInvoice,
  createReceipt,
  getCustomerRecentItems,
  getDocumentTypes,
  getPaymentTypes,
  getReceiptTypes,
  getRivhitItemPricing,
  getVatRate,
} from '@/api/client';
import { useRivhitData, rivhitDisplayName, type RivhitCustomer, type RivhitItem } from '@/context/RivhitDataContext';
import { pickBestCustomerPrice } from '@/lib/pricing';
import type { DocumentBuilderCardData } from '../types';
import type {
  CreatedDocument,
  DocType,
  PaymentLine,
  PaymentTypeItem,
  ReceiptType,
  SelectedCustomer,
  SelectedItem,
} from '@/pages/CreateDocument/types';

interface DocumentBuilderCardProps {
  data: DocumentBuilderCardData;
  onSelect: (selection: Record<string, unknown>) => void;
}

type BuilderMode = 'document' | 'receipt';

function matchDraftItem(draft: NonNullable<DocumentBuilderCardData['items']>[number], rivhitItems: RivhitItem[]) {
  return rivhitItems.find((item) => {
    if (draft.item_id && item.item_id === draft.item_id) return true;
    if (draft.catalog_number && item.catalog_number === draft.catalog_number) return true;
    if (draft.description && item.item_name?.toLowerCase() === draft.description.toLowerCase()) return true;
    return false;
  });
}

export function DocumentBuilderCard({ data, onSelect }: DocumentBuilderCardProps) {
  const { t } = useTranslation();
  const {
    configured,
    ensureCustomersLoaded,
    ensureItemsLoaded,
    searchCustomers,
    searchItems,
    customers,
    items: rivhitItems,
    itemsLoading,
    customersLoading,
  } = useRivhitData();

  const [mode, setMode] = useState<BuilderMode>(data.mode === 'receipt' ? 'receipt' : 'document');
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [receiptTypes, setReceiptTypes] = useState<ReceiptType[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeItem[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<number | null>(data.document_type ?? null);
  const [selectedReceiptType, setSelectedReceiptType] = useState<number | null>(data.receipt_type ?? null);
  const [priceIncludeVat, setPriceIncludeVat] = useState<boolean>(data.price_include_vat ?? true);
  const [customerQuery, setCustomerQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [itemQuery, setItemQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [payments, setPayments] = useState<PaymentLine[]>([]);
  const [comments, setComments] = useState(data.comments || '');
  const [vatRate, setVatRate] = useState<number | null>(null);
  const [recentItemIds, setRecentItemIds] = useState<Set<number>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const itemsHydratedRef = useRef(false);
  const paymentsHydratedRef = useRef(false);

  useEffect(() => {
    void ensureCustomersLoaded();
    void ensureItemsLoaded();
  }, [ensureCustomersLoaded, ensureItemsLoaded]);

  useEffect(() => {
    let cancelled = false;
    const loadRefData = async () => {
      try {
        const [dtRes, rtRes, ptRes, vatRes] = await Promise.all([
          getDocumentTypes(),
          getReceiptTypes(),
          getPaymentTypes(),
          getVatRate(),
        ]);
        if (cancelled) return;
        const nextDocTypes = dtRes.data ?? [];
        const nextReceiptTypes = (rtRes.data ?? []).filter((rt: ReceiptType) => !rt.is_invoice_receipt);
        const nextPaymentTypes = ptRes.data ?? [];
        const nextVatRate = vatRes.data?.vat_rate ?? vatRes.data?.rate ?? vatRes.data;

        setDocTypes(nextDocTypes);
        setReceiptTypes(nextReceiptTypes);
        setPaymentTypes(nextPaymentTypes);
        setVatRate(typeof nextVatRate === 'number' ? nextVatRate : null);

        if (!selectedDocumentType && nextDocTypes.length > 0) {
          setSelectedDocumentType(nextDocTypes[0].document_type);
          setPriceIncludeVat(nextDocTypes[0].price_include_vat ?? true);
        }
        if (!selectedReceiptType && nextReceiptTypes.length > 0) {
          setSelectedReceiptType(nextReceiptTypes[0].receipt_type);
        }
      } catch {
        // handled by API-layer errors on create path
      }
    };

    void loadRefData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!customers.length || selectedCustomer) return;
    const draftCustomer = data.customer;
    if (!draftCustomer) return;

    const matched = customers.find((customer) => {
      if (draftCustomer.customer_id != null && customer.customer_id === draftCustomer.customer_id) {
        return true;
      }
      if (draftCustomer.name) {
        return rivhitDisplayName(customer).toLowerCase() === draftCustomer.name.toLowerCase();
      }
      return false;
    });

    if (matched) {
      setSelectedCustomer({
        customer_id: matched.customer_id,
        display_name: rivhitDisplayName(matched),
        is_free_text: false,
      });
      setCustomerQuery(rivhitDisplayName(matched));
    } else if (draftCustomer.name) {
      setCustomerQuery(draftCustomer.name);
    }
  }, [customers, data.customer, selectedCustomer]);

  const resolveItemPrice = useCallback(
    async (itemId: number | undefined, quantity: number, fallbackPrice: number): Promise<number> => {
      if (!itemId || !selectedCustomer?.customer_id) return fallbackPrice;

      try {
        const response = await getRivhitItemPricing(itemId, selectedCustomer.customer_id);
        return pickBestCustomerPrice(response.data ?? [], quantity, fallbackPrice);
      } catch {
        return fallbackPrice;
      }
    },
    [selectedCustomer],
  );

  useEffect(() => {
    if (itemsHydratedRef.current || !configured || ((data.items?.length ?? 0) > 0 && !rivhitItems.length)) {
      return;
    }
    itemsHydratedRef.current = true;

    const hydrate = async () => {
      const draftItems = data.items ?? [];
      const nextItems = await Promise.all(
        draftItems.map(async (draft) => {
          const matched = matchDraftItem(draft, rivhitItems);
          const fallbackPrice = draft.price ?? matched?.price ?? 0;
          const quantity = draft.quantity || 1;
          const price = draft.is_custom
            ? fallbackPrice
            : await resolveItemPrice(matched?.item_id, quantity, fallbackPrice);

          return {
            id: crypto.randomUUID(),
            item_id: matched?.item_id ?? draft.item_id,
            catalog_number: matched?.catalog_number ?? draft.catalog_number,
            description: draft.description || matched?.item_name || '',
            quantity,
            price,
            unit: draft.unit || matched?.quantity_type || '',
            is_custom: draft.is_custom ?? !matched,
          } satisfies SelectedItem;
        }),
      );

      setSelectedItems(nextItems);
    };

    void hydrate();
  }, [configured, data.items, resolveItemPrice, rivhitItems]);

  useEffect(() => {
    if (paymentsHydratedRef.current) return;
    if ((data.payments?.length ?? 0) > 0 && paymentTypes.length === 0) return;

    paymentsHydratedRef.current = true;
    const draftPayments = data.payments ?? [];
    setPayments(
      draftPayments.length > 0
        ? draftPayments.map((payment, index) => ({
            payment_type: payment.payment_type ?? paymentTypes[0]?.payment_type ?? 0,
            payment_name:
              payment.payment_name ||
              paymentTypes.find((type) => type.payment_type === payment.payment_type)?.payment_name ||
              `${t('createDocument.payments.title', 'Payment')} ${index + 1}`,
            amount: payment.amount ?? 0,
          }))
        : [],
    );
  }, [data.payments, paymentTypes, t]);

  useEffect(() => {
    if (!selectedCustomer?.customer_id) {
      setRecentItemIds(new Set());
      return;
    }

    let cancelled = false;
    getCustomerRecentItems(selectedCustomer.customer_id)
      .then((res) => {
        if (!cancelled) {
          setRecentItemIds(new Set(res.data?.item_ids ?? []));
        }
      })
      .catch(() => {
        if (!cancelled) setRecentItemIds(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCustomer?.customer_id]);

  useEffect(() => {
    if (!selectedCustomer?.customer_id || selectedItems.length === 0) return;
    let cancelled = false;

    const repriceAll = async () => {
      const repriced = await Promise.all(
        selectedItems.map(async (item) => {
          if (item.is_custom) return item;
          const fallbackPrice = item.price || 0;
          const nextPrice = await resolveItemPrice(item.item_id, item.quantity, fallbackPrice);
          return { ...item, price: nextPrice };
        }),
      );
      if (!cancelled) setSelectedItems(repriced);
    };

    void repriceAll();
    return () => {
      cancelled = true;
    };
  }, [resolveItemPrice, selectedCustomer?.customer_id]);

  useEffect(() => {
    if (mode === 'receipt' && payments.length === 0 && paymentTypes.length > 0) {
      setPayments([
        {
          payment_type: paymentTypes[0].payment_type,
          payment_name: paymentTypes[0].payment_name,
          amount: 0,
        },
      ]);
    }
  }, [mode, paymentTypes, payments.length]);

  const customerResults = useMemo(() => {
    const query = customerQuery.trim();
    const result = query ? searchCustomers(query) : customers;
    return result.slice(0, 8);
  }, [customerQuery, customers, searchCustomers]);

  const itemResults = useMemo(() => {
    const query = itemQuery.trim();
    const result = query ? searchItems(query) : rivhitItems;
    return [...result]
      .sort((a, b) => {
        const aRecent = a.item_id != null && recentItemIds.has(a.item_id) ? 0 : 1;
        const bRecent = b.item_id != null && recentItemIds.has(b.item_id) ? 0 : 1;
        return aRecent - bRecent;
      })
      .slice(0, 10);
  }, [itemQuery, recentItemIds, rivhitItems, searchItems]);

  const selectedDocumentTypeRow = useMemo(
    () => docTypes.find((docType) => docType.document_type === selectedDocumentType),
    [docTypes, selectedDocumentType],
  );

  const selectedReceiptTypeRow = useMemo(
    () => receiptTypes.find((receiptType) => receiptType.receipt_type === selectedReceiptType),
    [receiptTypes, selectedReceiptType],
  );

  const subtotal = useMemo(
    () => selectedItems.reduce((sum, item) => sum + item.quantity * item.price, 0),
    [selectedItems],
  );
  const totalPayments = useMemo(
    () => payments.reduce((sum, payment) => sum + payment.amount, 0),
    [payments],
  );
  const vatAmount = vatRate != null ? subtotal * (vatRate / 100) : 0;
  const total = mode === 'receipt' ? totalPayments : priceIncludeVat ? subtotal : subtotal + vatAmount;

  const addRivhitItem = useCallback(
    async (rivhitItem: RivhitItem) => {
      const catalogNumber = rivhitItem.catalog_number || String(rivhitItem.item_id || '');
      if (!catalogNumber) return;

      const exists = selectedItems.some(
        (item) =>
          (item.item_id && rivhitItem.item_id && item.item_id === rivhitItem.item_id) ||
          (item.catalog_number && item.catalog_number === catalogNumber),
      );
      if (exists) return;

      const fallbackPrice = rivhitItem.price ?? 0;
      const price = await resolveItemPrice(rivhitItem.item_id, 1, fallbackPrice);
      setSelectedItems((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          item_id: rivhitItem.item_id,
          catalog_number: catalogNumber,
          description: rivhitItem.item_name || '',
          quantity: 1,
          price,
          unit: rivhitItem.quantity_type || '',
          is_custom: false,
        },
      ]);
      setItemQuery('');
    },
    [resolveItemPrice, selectedItems],
  );

  const updateItem = useCallback(
    async (index: number, patch: Partial<SelectedItem>) => {
      const currentItem = selectedItems[index];
      const nextItem = { ...currentItem, ...patch };

      if (!nextItem.is_custom) {
        const fallbackPrice = patch.price ?? nextItem.price;
        const repriced = await resolveItemPrice(nextItem.item_id, nextItem.quantity, fallbackPrice);
        nextItem.price = patch.price ?? repriced;
      }

      setSelectedItems((current) => current.map((item, itemIndex) => (itemIndex === index ? nextItem : item)));
    },
    [resolveItemPrice, selectedItems],
  );

  const addCustomItem = () => {
    setSelectedItems((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        description: t('createDocument.items.customItem', 'Custom Item'),
        quantity: 1,
        price: 0,
        unit: '',
        is_custom: true,
      },
    ]);
  };

  const addPayment = () => {
    const firstType = paymentTypes[0];
    if (!firstType) return;
    setPayments((current) => [
      ...current,
      {
        payment_type: firstType.payment_type,
        payment_name: firstType.payment_name,
        amount: 0,
      },
    ]);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);

    try {
      if (!selectedCustomer?.customer_id) {
        throw new Error(t('createDocument.customer.noResults', 'Please select a customer'));
      }

      let created: CreatedDocument;
      let typeName = '';

      if (mode === 'receipt') {
        if (!selectedReceiptType) throw new Error(t('createDocument.review.error', 'Failed to create document'));
        const validPayments = payments.filter((payment) => payment.amount > 0);
        if (validPayments.length === 0) {
          throw new Error(t('payments.addPayments', 'Please add at least one payment'));
        }

        const response = await createReceipt({
          receipt_type: selectedReceiptType,
          customer_id: selectedCustomer.customer_id,
          payments: validPayments.map((payment) => ({
            payment_type: payment.payment_type,
            amount_nis: payment.amount,
          })),
          comments: comments || undefined,
          send_mail: false,
        });
        created = response.data as CreatedDocument;
        typeName = selectedReceiptTypeRow?.receipt_name || t('createDocument.types.receipt', 'Receipt');
      } else {
        if (!selectedDocumentType) throw new Error(t('createDocument.review.error', 'Failed to create document'));
        if (selectedItems.length === 0) {
          throw new Error(t('createDocument.items.emptyState', 'No items added yet. Search products above or add a custom item.'));
        }

        const response = await createInvoice({
          document_type: selectedDocumentType,
          customer_id: selectedCustomer.customer_id,
          items: selectedItems.map((item) => ({
            item_id: item.item_id || undefined,
            catalog_number: item.catalog_number || undefined,
            quantity: item.quantity,
            description: item.description,
            price_nis: item.price,
          })),
          comments: comments || undefined,
          price_include_vat: priceIncludeVat,
          send_mail: false,
        });
        created = response.data as CreatedDocument;
        typeName = selectedDocumentTypeRow?.document_name || t('createDocument.title', 'Create Document');
      }

      onSelect({
        action: 'created',
        mode,
        document_type: created.document_type,
        document_number: created.document_number,
        document_link: created.document_link,
        customer_id: selectedCustomer.customer_id,
        customer_name: selectedCustomer.display_name,
        document_type_name: typeName,
        items_count: selectedItems.length,
        total,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('createDocument.review.error', 'Failed to create document'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="my-2 overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-sm">
      <div className="border-b border-emerald-100 bg-emerald-50 px-4 py-3">
        <p className="text-xs uppercase tracking-[0.18em] text-emerald-600">
          {t('createDocument.title', 'Create Document')}
        </p>
        <h3 className="mt-1 text-sm font-semibold text-slate-900">
          {data.title || t('createDocument.subtitle', 'Quickly create invoices, receipts, and delivery notes')}
        </h3>
        {(data.context || data.description) && (
          <p className="mt-1 text-xs text-slate-500">{data.context || data.description}</p>
        )}
      </div>

      <div className="space-y-4 px-4 py-4">
        {!configured && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Rivhit is not configured.
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode('document')}
            className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
              mode === 'document'
                ? 'border-primary-400 bg-primary-50 text-primary-700'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            {t('createDocument.title', 'Create Document')}
          </button>
          <button
            type="button"
            onClick={() => setMode('receipt')}
            className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
              mode === 'receipt'
                ? 'border-primary-400 bg-primary-50 text-primary-700'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            {t('createDocument.types.receipt', 'Receipt')}
          </button>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            {t('createDocument.review.docType', 'Document Type')}
          </label>
          {mode === 'receipt' ? (
            <select
              value={selectedReceiptType ?? ''}
              onChange={(e) => setSelectedReceiptType(Number(e.target.value) || null)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400"
            >
              {receiptTypes.map((type) => (
                <option key={type.receipt_type} value={type.receipt_type}>
                  {type.receipt_name}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={selectedDocumentType ?? ''}
              onChange={(e) => {
                const next = Number(e.target.value) || null;
                setSelectedDocumentType(next);
                const typeRow = docTypes.find((type) => type.document_type === next);
                if (typeRow) setPriceIncludeVat(typeRow.price_include_vat ?? true);
              }}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400"
            >
              {docTypes.map((type) => (
                <option key={type.document_type} value={type.document_type}>
                  {type.document_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            {t('createDocument.review.customer', 'Customer')}
          </label>
          <input
            type="text"
            value={customerQuery}
            onChange={(e) => {
              setCustomerQuery(e.target.value);
              setSelectedCustomer(null);
            }}
            placeholder={t('createDocument.customer.searchPlaceholder', 'Search customers by name, phone, or email...')}
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-primary-400"
          />
          {(customerQuery.trim() || customersLoading) && (
            <div className="mt-2 max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white">
              {customersLoading ? (
                <p className="px-3 py-2 text-sm text-slate-500">{t('common.loading', 'Loading...')}</p>
              ) : customerResults.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-500">{t('createDocument.customer.noResults', 'No matching customers found')}</p>
              ) : (
                customerResults.map((customer: RivhitCustomer) => (
                  <button
                    key={customer.customer_id}
                    type="button"
                    onClick={() => {
                      setSelectedCustomer({
                        customer_id: customer.customer_id,
                        display_name: rivhitDisplayName(customer),
                        is_free_text: false,
                      });
                      setCustomerQuery(rivhitDisplayName(customer));
                    }}
                    className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50"
                  >
                    <span className="font-medium text-slate-800">{rivhitDisplayName(customer)}</span>
                    <span className="text-xs text-slate-400">#{customer.customer_id}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {mode === 'document' && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                {t('createDocument.review.items', 'Items')}
              </label>
              <input
                type="text"
                value={itemQuery}
                onChange={(e) => setItemQuery(e.target.value)}
                placeholder={t('createDocument.items.searchPlaceholder', 'Search products by name, catalog number...')}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-primary-400"
              />
              {(itemQuery.trim() || itemsLoading || recentItemIds.size > 0) && (
                <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                  {itemsLoading ? (
                    <p className="px-3 py-2 text-sm text-slate-500">{t('createDocument.items.loadingItems', 'Loading items from Rivhit...')}</p>
                  ) : itemResults.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-slate-500">{t('common.noResults', 'No results found')}</p>
                  ) : (
                    itemResults.map((item) => (
                      <button
                        key={`${item.item_id}-${item.catalog_number}`}
                        type="button"
                        onClick={() => void addRivhitItem(item)}
                        className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50"
                      >
                        <div>
                          <p className="font-medium text-slate-800">{item.item_name}</p>
                          <p className="text-xs text-slate-500">{item.catalog_number || `#${item.item_id}`}</p>
                        </div>
                        {item.item_id != null && recentItemIds.has(item.item_id) && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            {t('createDocument.items.recent', 'Recent')}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              {selectedItems.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
                  {t('createDocument.items.emptyState', 'No items added yet. Search products above or add a custom item.')}
                </div>
              ) : (
                selectedItems.map((item, index) => (
                  <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,2fr)_90px_110px_80px_auto]">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => void updateItem(index, { description: e.target.value, is_custom: true })}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                      />
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={item.quantity}
                        onChange={(e) => void updateItem(index, { quantity: Number(e.target.value) || 0 })}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                      />
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={item.price}
                        onChange={(e) => void updateItem(index, { price: Number(e.target.value) || 0, is_custom: item.is_custom })}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                      />
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => void updateItem(index, { unit: e.target.value })}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                        className="rounded-lg bg-white px-3 py-1.5 text-sm text-red-600 transition hover:bg-red-50"
                      >
                        {t('createDocument.items.remove', 'Remove')}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {t('createDocument.items.lineTotal', 'Total')}: ₪{(item.quantity * item.price).toFixed(2)}
                    </p>
                  </div>
                ))
              )}
              <button
                type="button"
                onClick={addCustomItem}
                className="w-full rounded-xl border border-dashed border-primary-300 px-3 py-2 text-sm font-medium text-primary-700 transition hover:bg-primary-50"
              >
                {t('createDocument.items.addCustom', 'Add Custom Item')}
              </button>
            </div>
          </>
        )}

        {mode === 'receipt' && (
          <div className="space-y-2">
            {payments.map((payment, index) => (
              <div key={`${payment.payment_type}-${index}`} className="grid gap-2 sm:grid-cols-[minmax(0,2fr)_120px_auto]">
                <select
                  value={payment.payment_type}
                  onChange={(e) => {
                    const nextType = Number(e.target.value);
                    const typeRow = paymentTypes.find((type) => type.payment_type === nextType);
                    setPayments((current) =>
                      current.map((row, rowIndex) =>
                        rowIndex === index
                          ? {
                              ...row,
                              payment_type: nextType,
                              payment_name: typeRow?.payment_name || row.payment_name,
                            }
                          : row,
                      ),
                    );
                  }}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400"
                >
                  {paymentTypes.map((type) => (
                    <option key={type.payment_type} value={type.payment_type}>
                      {type.payment_name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={payment.amount}
                  onChange={(e) => {
                    const nextAmount = Number(e.target.value) || 0;
                    setPayments((current) =>
                      current.map((row, rowIndex) => (rowIndex === index ? { ...row, amount: nextAmount } : row)),
                    );
                  }}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400"
                />
                <button
                  type="button"
                  onClick={() => setPayments((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                  className="rounded-xl bg-white px-3 py-2 text-sm text-red-600 transition hover:bg-red-50"
                >
                  {t('createDocument.items.remove', 'Remove')}
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPayment}
              className="w-full rounded-xl border border-dashed border-primary-300 px-3 py-2 text-sm font-medium text-primary-700 transition hover:bg-primary-50"
            >
              {t('createDocument.payments.addPayment', 'Add Payment')}
            </button>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            {t('createDocument.review.comments', 'Comments')}
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={t('createDocument.review.commentsPlaceholder', 'Add any comments for this document...')}
            rows={3}
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400"
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
          {mode === 'receipt' ? (
            <div className="flex items-center justify-between font-semibold text-slate-800">
              <span>{t('createDocument.review.total', 'Total')}</span>
              <span>₪{total.toFixed(2)}</span>
            </div>
          ) : (
            <div className="space-y-1 text-slate-700">
              <div className="flex items-center justify-between">
                <span>{t('createDocument.review.subtotal', 'Subtotal')}</span>
                <span>₪{subtotal.toFixed(2)}</span>
              </div>
              {!priceIncludeVat && vatRate != null && (
                <div className="flex items-center justify-between">
                  <span>{t('createDocument.review.vat', 'VAT')}</span>
                  <span>₪{vatAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between font-semibold text-slate-900">
                <span>{t('createDocument.review.total', 'Total')}</span>
                <span>₪{total.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onSelect({ action: 'cancel' })}
            className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating || !configured}
            className="flex-1 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
          >
            {creating ? t('createDocument.review.creating', 'Creating document...') : t('createDocument.review.create', 'Create Document')}
          </button>
        </div>
      </div>
    </div>
  );
}
